<template>
  <div class="product-list">
    <!-- 顶部提示栏（红色边框） -->
    <div class="tip-bar">
      <span class="tip-text"
        >基于您的购买与销售记录，盘雷系统会在销售大厅及公共区域，为您推送相关的特价产品信息或专属客户资讯。</span
      >
    </div>

    <!-- 筛选栏 -->
    <div class="filter-bar">
      <div class="filter-left">
        <span class="total-count">
          <span>品牌 <span class="count">{{ page.total }}</span></span>
          <span class="count-divider">|</span>
          <span>商品 <span class="count">{{ goodsTotal }}</span></span>
        </span>
        <div class="filter-item">
          <span class="filter-label">时间：</span>
          <el-date-picker
            v-model="filterTime"
            class="filter-date"
            type="month"
            placeholder="选择月份"
            value-format="YYYY-MM"
            @change="handleSearch"
          />
        </div>
        <div class="filter-item">
          <span class="label">品牌：</span>
          <el-select v-model="filterBrand" class="filter-brand" placeholder="请选择品牌" clearable filterable @change="handleSearch">
            <el-option v-for="item in brandOptions" :key="item.id" :label="item.categoryName" :value="item.id" />
          </el-select>
        </div>
        <el-input v-model="filterKeyword" class="filter-keyword" placeholder="请输入品牌、商品名称、厂家、系列或型号搜索" clearable @keyup.enter="handleSearch">
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
        <!-- 类型查询暂时隐藏，待后续功能完善后启用 -->
        <!-- <el-select v-model="filterType" class="filter-type" placeholder="增加类型">
          <el-option label="销售信息导入" value="import" />
          <el-option label="新增" value="add" />
        </el-select> -->
        <el-button @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </div>
      <el-button type="primary" @click="handleAddBrand">新增品牌</el-button>
    </div>

    <!-- 数据表格：品牌一对多商品 -->
    <el-table v-loading="loading" :data="tableData" style="width: 100%" border>
      <el-table-column label="序号" width="80" align="center">
        <template #default="{ $index }">
          <span>{{ $index + 1 }}</span>
        </template>
      </el-table-column>
      <el-table-column label="品牌信息" min-width="280">
        <template #default="{ row }">
          <div class="product-info">
            <div class="product-title">
              <span class="product-name">{{ row.name }}</span>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="商品列表" min-width="520">
        <template #default="{ row }">
          <div class="goods-list">
            <div v-for="goods in row.goodsList" :key="goods.id" class="goods-card">
              <div class="goods-main">
                <div class="goods-name">{{ goods.name }}</div>
                <div class="goods-detail">
                  <span>型号：{{ goods.model }}</span>
                  <span>订货号：{{ goods.orderNo }}</span>
                  <span>包装规格：{{ goods.package }}</span>
                  <span>面价：{{ goods.price }}</span>
                </div>
                <div class="goods-detail muted">
                  <span>购买次数：{{ goods.buyCount }}</span>
                  <span>最近购买日期：{{ goods.lastBuyTime }}</span>
                </div>
              </div>
              <el-text class="el-text" @click="handleDeleteGoods(row, goods)">删除</el-text>
            </div>
            <el-empty v-if="!row.goodsList.length" description="暂无商品" :image-size="60" />
          </div>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="150" align="center">
        <template #default="{ row }">
          <div class="action-group">
            <el-text class="el-text" @click="handleAddGoods(row)">新增商品</el-text>
            <el-text class="delete-text" @click="handleDeleteBrand(row)">删除品牌</el-text>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-bar">
      <el-pagination
        v-model:current-page="page.current"
        v-model:page-size="page.size"
        :total="page.total"
        :page-sizes="[10, 20, 50]"
        layout="total, sizes, prev, pager, next, jumper"
        @current-change="handleCurrentChange"
        @size-change="handleSizeChange"
      />
    </div>
  </div>

  <add-brand-dialog v-model="showAddBrand" :emption-type="0" @success="handleAddBrandSuccess" />
  <add-goods-dialog
    :key="goodsDialogKey"
    v-model="showAddGoods"
    :parent-id="currentBrand?.id || ''"
    :emption-type="'0'"
    :ids="currentGoodsIds"
    @success="handleAddGoodsSuccess"
  />
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import {
  ElButton,
  ElDatePicker,
  ElEmpty,
  ElInput,
  ElMessage,
  ElMessageBox,
  ElSelect,
  ElOption,
  ElTable,
  ElTableColumn,
  ElIcon,
  ElPagination
} from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import AddBrandDialog from '../../myMessageModule/components/dialog/addBrand.vue'
import AddGoodsDialog from './dialog/addGoods.vue'

const store = useStore()

// 筛选条件
const filterTime = ref('')
const filterBrand = ref('')
const filterKeyword = ref('')
const filterType = ref('')
const showAddBrand = ref(false)
const showAddGoods = ref(false)
const currentBrand = ref(null)
const goodsDialogKey = ref(0)
const loading = ref(false)
const brandOptions = ref([])

const page = ref({
  current: 1,
  size: 10,
  total: 0
})

// 表格数据：品牌下挂多个商品，品牌分页/删除已接接口，商品后续接入时填充 goodsList。
const tableData = ref([])

const goodsTotal = computed(() => tableData.value.reduce((total, product) => total + product.goodsList.length, 0))
const currentGoodsIds = computed(() => (currentBrand.value?.goodsList || []).map(goods => goods.skuId || goods.id))

const formatGoodsRow = row => {
  return {
    ...row,
    id: row.id || row.skuId,
    name: row.productName || row.name || '-',
    model: row.barCode || row.model || '无',
    orderNo: row.articleNo || row.orderNo || '无',
    package: row.packageSpec || row.package || '无',
    price: row.guidePrice || row.price || '无',
    buyCount: row.buyCount || row.purchaseCount || 0,
    lastBuyTime: row.lastBuyTime || row.lastPurchaseTime || '-'
  }
}

const formatBrandRow = row => {
  return {
    ...row,
    name: row.brandName || row.name || '-',
    goodsList: (row.goodsList || row.skuList || row.productList || []).map(formatGoodsRow)
  }
}

const getList = () => {
  loading.value = true
  store
    .dispatch('myMessageModule/userPageEmption', {
      pageNo: page.value.current,
      pageSize: page.value.size,
      type: '0',
      brandId: filterBrand.value || undefined,
      keyword: filterKeyword.value || undefined,
      createMonth: filterTime.value || undefined
    })
    .then(res => {
      const { code, data } = res.data
      if (code === '0') {
        tableData.value = (data.rows || []).map(formatBrandRow)
        page.value.total = data.totalCount || 0
      }
    })
    .finally(() => {
      loading.value = false
    })
}

// 加载品牌下拉列表
const loadBrandOptions = () => {
  store.dispatch('myMessageModule/listSkuCategoryForFirst', {
    pageNo: 1,
    pageSize: 9999
  }).then(res => {
    const { code, data } = res.data
    if (code === '0') {
      brandOptions.value = data.data || data.rows || []
    }
  })
}

// 搜索
const handleSearch = () => {
  page.value.current = 1
  getList()
}

// 重置筛选
const handleReset = () => {
  filterTime.value = ''
  filterBrand.value = ''
  filterKeyword.value = ''
  filterType.value = ''
  page.value.current = 1
  getList()
}

// 新增品牌
const handleAddBrand = () => {
  showAddBrand.value = true
}

// 新增品牌成功
const handleAddBrandSuccess = () => {
  page.value.current = 1
  getList()
}

// 新增商品
const handleAddGoods = product => {
  currentBrand.value = product
  goodsDialogKey.value += 1
  showAddGoods.value = true
}

// 新增商品成功
const handleAddGoodsSuccess = () => {
  getList()
}

// 删除商品
const handleDeleteGoods = (product, goods) => {
  ElMessageBox.confirm('是否删除该商品?', '提示', {
    confirmButtonText: '确认',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(() => {
      return store.dispatch('myMessageModule/userDelEmption', goods.id)
    })
    .then(res => {
      const { code } = res.data
      if (code === '0') {
        ElMessage({ type: 'success', message: '删除成功' })
        getList()
      }
    })
    .catch(() => {})
}

// 删除品牌
const handleDeleteBrand = product => {
  ElMessageBox.confirm('是否删除该品牌?', '提示', {
    confirmButtonText: '确认',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(() => {
      return store.dispatch('myMessageModule/userDelEmption', product.id)
    })
    .then(res => {
      const { code } = res.data
      if (code === '0') {
        ElMessage({ type: 'success', message: '删除成功' })
        if (tableData.value.length === 1 && page.value.current > 1) {
          page.value.current -= 1
        }
        getList()
      }
    })
    .catch(() => {})
}

const handleCurrentChange = current => {
  page.value.current = current
  getList()
}

const handleSizeChange = size => {
  page.value.size = size
  page.value.current = 1
  getList()
}

onMounted(() => {
  loadBrandOptions()
  getList()
})
</script>

<style scoped lang="less">
.product-list {
  padding: 24px;
  background: #fff;
}

/* 顶部提示栏 */
.tip-bar {
  border: 1px solid #f56c6c;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 24px;
  background-color: #fef0f0;
}
.tip-text {
  font-size: 14px;
  color: #606266;
}

/* 筛选栏 */
.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}
.filter-left {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  flex: 1;
  gap: 8px;
  min-width: 0;
  overflow: visible;
}
.total-count {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  height: 32px;
  border-radius: 16px;
  background: #f5f7fa;
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}
.count {
  color: #f56c6c;
  font-weight: 600;
}
.count-divider {
  color: #dcdfe6;
}
.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.label {
  font-size: 14px;
  color: #606266;
}
.filter-label {
  white-space: nowrap;
}
.filter-date {
  width: 170px;
}
.filter-brand {
  width: 180px;
}
.filter-keyword {
  width: 280px;
}
.filter-type {
  width: 130px;
}

/* 筛选栏输入框图标居中 */
:deep(.filter-brand .el-input__suffix),
:deep(.filter-brand .el-input__prefix) {
  display: flex;
  align-items: center;
}
:deep(.filter-keyword .el-input__suffix),
:deep(.filter-keyword .el-input__prefix) {
  display: flex;
  align-items: center;
}

/* 品牌信息列 */
.product-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.product-title {
  display: flex;
  align-items: center;
}
.product-name {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}

/* 商品列表 */
.goods-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.goods-card {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 12px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #fbfcfd;
}
.goods-main {
  min-width: 0;
}
.goods-name {
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 500;
  color: #303133;
}
.goods-detail {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  font-size: 13px;
  line-height: 20px;
  color: #606266;
}
.muted {
  margin-top: 4px;
  color: #909399;
}
.action-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

/* 表格样式 */
:deep(.el-table) {
  --el-table-border-color: #e4e7ed;
}
:deep(.el-table th) {
  background-color: #f5f7fa;
}
.el-text {
  color: #48a09c;
  cursor: pointer;
}
.delete-text {
  color: #f56c6c;
  cursor: pointer;
}
.pagination-bar {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}
</style>
